import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  OutlinedInputProps,
  CircularProgress,
  InputAdornment,
  IconButton,
  styled
} from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';

import { debounce } from 'lodash';
import { SearchOption, SearchType } from './interfaces';
import { IntelligenceSearch } from './styled';
import { IntelligenceIcon, SearchIcon } from '../../sicons';
import { IntelligenceSearchPopup, SearchDropdown } from './components';
import { SearchFormOptions } from '../product-match/enums';
import { useCatalogStore } from '../../../stores';
import { TextColor } from '../../../enums';
import { AIGradientBorderColor } from '../styled';

export const Container = styled('div')({
  padding: 2,
  borderRadius: 25,
  background: ({ aiSearchActive }: { aiSearchActive: boolean }) =>
    aiSearchActive ? AIGradientBorderColor : 'transparent',
  display: 'inline-block',
  '& fieldset': {
    // border: ({ aiSearchActive }: { aiSearchActive: boolean }) =>
    //   aiSearchActive && 'none !important'
  }
});

export const options: SearchOption[] = [
  {
    label: 'Search',
    value: SearchType.Regular,
    icon: <SearchIcon style={{ fontSize: '1.15rem' }} />
  },
  {
    label: 'Ask AI',
    value: SearchType.AI,
    icon: <IntelligenceIcon fontSize="small" />
  }
];
export interface AISearch {
  value: string;
  [key: string]: string | boolean;
}

interface ISearchProps extends OutlinedInputProps {
  aiSearchActive?: boolean;
  aiEnabled?: boolean;
  type?: SearchFormOptions;
  loading?: boolean;
  searchQuery?: AISearch;
  defaultOption?: SearchOption;
  onClose?: () => void;
  onClear?: () => void;
  handleSearch: (val: {
    type: SearchType;
    search: AISearch;
    aiType?: SearchFormOptions;
  }) => void;
}

const ISearch = ({
  type,
  loading,
  aiEnabled = true,
  searchQuery,
  defaultOption,
  aiSearchActive,

  onClose,
  onClear,
  handleSearch
}: ISearchProps) => {
  const flags = useFlags();
  const { isAISearchActive, setIsAISearchActive } = useCatalogStore();

  const aiSearch = useMemo(
    () => flags?.productsProductMatch && aiEnabled,
    [aiEnabled, flags?.productsProductMatch]
  );

  const [currType, setCurrType] = useState<SearchFormOptions>(
    type || SearchFormOptions.text
  );
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  const [option, setOption] = useState(defaultOption || options[0]);
  const [popupAnchorEl, setPopupAnchorEl] = useState<null | HTMLElement>(null);
  const { value, ...searchedValues } = searchQuery;

  useEffect(() => {
    if (!value?.trim() && value !== search) setSearch(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    debounce(
      (val: string) =>
        handleSearch({
          type: SearchType.Regular,
          search: {
            value: val
          }
        }),
      700
    ),
    [handleSearch]
  );

  const handleInputClear = useCallback(() => {
    setSearch('');

    if (option === options[0]) {
      handleSearch({ type: SearchType.Regular, search: { value: '' } });
      setIsAISearchActive(false);
    } else {
      onClear?.();
      setPopupAnchorEl(wrapperRef.current);
      setIsAISearchActive(true);
    }
    // setOption(options[0]);
    handleChange.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSearch]);

  const endAdornment = useMemo(() => {
    if (loading) return <CircularProgress size={24} />;
    if (search)
      return (
        <span onClick={e => e.stopPropagation()}>
          <IconButton
            onClick={handleInputClear}
            style={{
              padding: 0,
              margin: 0
            }}>
            <CloseRoundedIcon
              htmlColor={TextColor.SlateGrey}
              style={{
                width: 16,
                height: 16
              }}
            />
          </IconButton>
        </span>
      );

    return aiSearch ? null : <SearchIcon style={{ fontSize: '1.15rem' }} />;
  }, [handleInputClear, aiSearch, loading, search]);

  useEffect(() => {
    if (type) {
      setSearch(value || '');
      setCurrType(type || SearchFormOptions.text);
    }
  }, [type, value]);

  useEffect(() => {
    if (isAISearchActive) {
      setOption(options[1]);
      setCurrType(SearchFormOptions.text);

      setTimeout(() => {
        setPopupAnchorEl(wrapperRef.current);
      }, 5);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAISearchActive, type]);

  useEffect(() => {
    setOption(defaultOption);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOption]);

  const handleClose = () => {
    onClose?.();
    setPopupAnchorEl(null);

    if (option === options[1] && isAISearchActive) {
      setOption(options[0]);
      setIsAISearchActive(false);
    }

    handleChange.cancel();
  };

  const wrapperStyle = useMemo(
    () => ({
      padding: 2,
      borderRadius: 9999,
      background:
        option === options[1]
          ? 'linear-gradient(180deg, #1E96FC 0%, #7E44FC 31.5%, #57CCF0 100%)'
          : 'transparent',
      display: 'inline-block',
      '&:hover': {
        opacity: 0.5
      }
    }),
    [option]
  );

  return (
    <Container
      ref={wrapperRef}
      style={wrapperStyle}
      aiSearchActive={option === options[1]}>
      <IntelligenceSearch
        value={search}
        onClick={
          option.value === SearchType.AI && aiSearch
            ? () => setPopupAnchorEl(wrapperRef.current)
            : undefined
        }
        // Pass any additional props you want, e.g. value, onChange, etc.
        onChange={e => {
          setSearch(e.target.value);
          handleChange(e.target.value);
        }}
        // Start adornment: Search icon + "Search" text + dropdown arrow
        startAdornment={
          aiSearch && (
            <InputAdornment position="start">
              <SearchDropdown
                disabled={option === options[1] && (aiSearchActive || loading)}
                options={options}
                onSelect={o => {
                  setOption(o);
                  setIsAISearchActive(o === options[1]);
                  if (o === options[1]) setPopupAnchorEl(wrapperRef.current);
                }}
                selectedOption={option}
              />
            </InputAdornment>
          )
        }
        endAdornment={
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        }
        placeholder={
          option.value === SearchType.AI
            ? 'Ask me anything...'
            : 'Enter keywords to search...'
        }
      />

      {!!popupAnchorEl && (
        <IntelligenceSearchPopup
          defaultType={currType}
          anchorEl={popupAnchorEl}
          search={searchedValues}
          onClear={onClear}
          setAnchorEl={() => {
            setPopupAnchorEl(null);
            setIsAISearchActive(false);
          }}
          onClose={handleClose}
          onSubmit={(newAIType, searchValue) => {
            setSearch(searchValue.value);
            setPopupAnchorEl(null);
            setIsAISearchActive(false);
            handleSearch({
              type: SearchType.AI,
              aiType: newAIType,
              search: searchValue
            });
          }}
        />
      )}
    </Container>
  );
};

export default ISearch;
